// In-house replacement for the third-party 'angular-svg-icon' package, which stopped
// releasing versions after Angular 19 (github.com/czeckd/angular-svg-icon issue #173,
// requested and never fulfilled). This keeps the exact `<svg-icon [src] [svgClass]>`
// template API the rest of the app already uses, so no consuming component needs to
// change - only the module specifier is redirected here via tsconfig `paths`.
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EnvironmentProviders,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
  inject,
  makeEnvironmentProviders,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay } from 'rxjs';

const svgTextCache = new Map<string, Observable<string>>();

@Component({
  selector: 'svg-icon',
  standalone: true,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SvgIconComponent implements OnChanges {
  @Input() src?: string;
  @Input() svgClass?: string;

  private http = inject(HttpClient);
  private el = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);
  private currentSvg?: SVGElement;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src']) {
      this.load();
    } else if (changes['svgClass'] && this.currentSvg) {
      this.applyClass(this.currentSvg, changes['svgClass'].previousValue, this.svgClass);
    }
  }

  private load(): void {
    const src = this.src;
    if (!src) return;

    let svgText$ = svgTextCache.get(src);
    if (!svgText$) {
      svgText$ = this.http.get(src, { responseType: 'text' }).pipe(shareReplay(1));
      svgTextCache.set(src, svgText$);
    }
    svgText$.subscribe({
      next: (svgText) => this.render(svgText),
      error: () => {},
    });
  }

  private render(svgText: string): void {
    if (this.currentSvg) {
      this.renderer.removeChild(this.el.nativeElement, this.currentSvg);
      this.currentSvg = undefined;
    }

    const svgEl = this.parse(svgText);
    if (!svgEl) return;

    this.applyEncapsulationAttribute(svgEl);
    this.applyClass(svgEl, undefined, this.svgClass);
    this.renderer.appendChild(this.el.nativeElement, svgEl);
    this.currentSvg = svgEl;
  }

  // Emulated encapsulation scopes a component's styles with an `_ngcontent-*` attribute, which
  // Angular can only stamp on elements it renders itself. The <svg> is injected here at runtime,
  // so without copying the host's attribute onto it every `svgClass` whose rule lives in a
  // component's SCSS (rather than in the global stylesheet) silently matches nothing.
  private applyEncapsulationAttribute(target: SVGElement): void {
    const host = this.el.nativeElement as HTMLElement;
    const scope = Array.from(host.attributes).find((a) => a.name.startsWith('_ngcontent-'));
    if (scope) target.setAttribute(scope.name, scope.value);
  }

  // Angular's HTML sanitizer can't be used here: it consciously drops every SVG element
  // (`<svg>`, `<path>`, `<g>`, `<mask>`, ...) since none of them are on its allow-list, so
  // sanitizing an icon leaves an empty string and nothing ever gets rendered. The icons are
  // first-party assets served from our own /assets, so they're parsed as SVG and scrubbed of
  // the active parts (scripts, event handlers, javascript: links) instead.
  private parse(svgText: string): SVGElement | null {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    if (doc.getElementsByTagName('parsererror').length) return null;

    const root = doc.documentElement;
    if (!root || root.nodeName.toLowerCase() !== 'svg') return null;

    this.scrub(root);
    return document.importNode(root, true) as unknown as SVGElement;
  }

  private scrub(el: Element): void {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const isLink = name === 'href' || name === 'xlink:href';
      if (name.startsWith('on') || (isLink && /^\s*javascript:/i.test(attr.value))) {
        el.removeAttribute(attr.name);
      }
    }

    for (const child of Array.from(el.children)) {
      const tag = child.nodeName.toLowerCase();
      if (tag === 'script' || tag === 'foreignobject') {
        child.remove();
        continue;
      }
      this.scrub(child);
    }
  }

  private applyClass(target: SVGElement, previous: string | undefined, current: string | undefined): void {
    if (previous) {
      for (const k of previous.split(' ').filter(Boolean)) this.renderer.removeClass(target, k);
    }
    if (current) {
      for (const k of current.split(' ').filter(Boolean)) this.renderer.addClass(target, k);
    }
  }
}

export function provideAngularSvgIcon(): EnvironmentProviders {
  return makeEnvironmentProviders([]);
}
